import { useState } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import SendIcon from '@mui/icons-material/Send';
import InsertPersonalFieldsDialog from './InsertPersonalFieldsDialog';
import SmsPhoneMockup from './SmsPhoneMockup';
import SmsOfferCodeDialog from './SmsOfferCodeDialog';
import BatPreviewDialog from './BatPreviewDialog';
import { isRichSms } from './creationPayload';
import { notifyOfferTokenInserted, offerTokenChoices } from './creation/shared/offerTokens';
import { CreationPayload, InitialPayload, readString } from './creation/shared/payloadReaders';
import { outlinedButtonSx, toolbarSx } from './creation/shared/buttonStyles';

interface SmsCreationSectionProps {
  offerCount?: number;
  offerTitles?: string[];
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
  initialPayload?: InitialPayload;
  onPayloadChange?: (payload: CreationPayload) => void;
}

interface OfferDialogState {
  open: boolean;
  codes: string[];
  titles?: string[];
}

function SmsCreationSection({ offerCount = 1, offerTitles, onAddOffer, onOfferUsed, initialPayload, onPayloadChange }: SmsCreationSectionProps) {
  const theme = useTheme();
  const [message, setMessage] = useState(() => readString(initialPayload, 'message'));
  const [enrichi, setEnrichi] = useState(() => isRichSms(initialPayload));
  const [personalFieldsOpen, setPersonalFieldsOpen] = useState(false);
  const [offerDialog, setOfferDialog] = useState<OfferDialogState>({ open: false, codes: [] });
  const [batOpen, setBatOpen] = useState(false);

  const update = (nextMessage: string, nextEnrichi: boolean) => {
    setMessage(nextMessage);
    setEnrichi(nextEnrichi);
    onPayloadChange?.({ type: 'sms', message: nextMessage, is_rich_sms: nextEnrichi ? 1 : 0 });
  };

  const insertToken = (token: string) => {
    update(message + token, enrichi);
    setOfferDialog(state => ({ ...state, open: false }));
    notifyOfferTokenInserted(token, { offerCount, onOfferUsed, onAddOffer });
  };

  const buttonSx = outlinedButtonSx(theme.palette.primary.main);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ ...toolbarSx, mb: 3 }}>
        <Tooltip title="Insérer un champ personnalisé">
          <Button variant="outlined" size="small" startIcon={<PersonAddAltIcon fontSize="small" />} onClick={() => setPersonalFieldsOpen(true)} sx={buttonSx}>
            Champ de personnalisation
          </Button>
        </Tooltip>
        <Tooltip title="Insérer un code d'opération (offre)">
          <Button
            variant="outlined"
            size="small"
            startIcon={<ConfirmationNumberIcon fontSize="small" />}
            onClick={() => setOfferDialog({ open: true, codes: offerTokenChoices(offerCount), titles: offerTitles })}
            sx={buttonSx}
          >
            Code opération
          </Button>
        </Tooltip>
        <Tooltip title="Envoyer un BAT de ce SMS">
          <span>
            <Button variant="outlined" size="small" startIcon={<SendIcon fontSize="small" />} onClick={() => setBatOpen(true)} disabled={!message.trim()} sx={buttonSx}>
              BAT
            </Button>
          </span>
        </Tooltip>
      </Box>

      <SmsPhoneMockup
        message={message}
        enrichi={enrichi}
        onMessageChange={value => update(value, enrichi)}
        onEnrichiChange={value => update(message, value)}
      />

      <InsertPersonalFieldsDialog
        open={personalFieldsOpen}
        onClose={() => setPersonalFieldsOpen(false)}
        onInsert={insertToken}
        channel="sms"
      />

      <BatPreviewDialog
        open={batOpen}
        onClose={() => setBatOpen(false)}
        content={{ html: message, subject: '' }}
        channel="SMS"
      />

      <SmsOfferCodeDialog
        open={offerDialog.open}
        onClose={() => setOfferDialog(state => ({ ...state, open: false }))}
        offerCodes={offerDialog.codes}
        offerTitles={offerDialog.titles}
        onSelect={insertToken}
      />
    </Box>
  );
}

export default SmsCreationSection;
